import {
  mdiAlertCircleOutline,
  mdiCheckCircleOutline,
  mdiEmailOutline,
  mdiPhoneOutline,
} from "@mdi/js"
import { formatContactAddress } from "../contact-addresses"
import {
  formatPropertyAddressLinesWithUnit,
  formatPropertyUnitDisplayForProperty,
} from "../../../../../_shared/_logic/unit-labels"
import {
  buildRichTooltip,
  tooltipField,
  tooltipTitle,
} from "../../../../../_shared/_logic/rich-tooltips"
import { isPhoneContactPointKind } from "../contact-point-kinds"

export type ContactTagColor = "neutral"

export type ContactMetaTagPresentation = {
  label: string
  color: string
  tooltip: string
}

export type ContactPointMetaTooltipField = {
  label: string
  value: string
}

export type ContactTagPresentation = {
  id: string
  label: string
  roleLabel?: string | null
  nameLabel?: string | null
  subLabel?: string | null
  icon: string
  color: ContactTagColor
  tooltip: string
  tooltipHtml?: boolean
}

export type ContactPointLike = {
  id?: string | null
  customer_id?: string | null
  kind?: string | null
  value?: string | Record<string, unknown> | null
  normalized_value?: string | null
  is_verified?: boolean | null
  source?: string | null
  lifecycle_status?: string | null
  created_at?: string | null
  source_import_draft_id?: string | null
  last_seen_import_draft_id?: string | null
  last_seen_in_import_at?: string | null
  source_record_key?: string | null
  last_success_at?: string | null
  last_failure_at?: string | null
  last_failure_reason?: string | null
}

type CustomerLike = {
  id?: string | null
  display_name?: string | null
}

const normalizeKind = (value: unknown) => `${value ?? ""}`.trim().toUpperCase()

const normalizeText = (value: unknown) => `${value ?? ""}`.trim()

const lifecycleStatus = (point: ContactPointLike) =>
  normalizeText(point.lifecycle_status || "ACTIVE").toUpperCase()

const sourceValue = (point: ContactPointLike) => normalizeText(point.source || "").toUpperCase()

const hasImportMetadata = (point: ContactPointLike) =>
  Boolean(
    normalizeText(point.source_import_draft_id) ||
    normalizeText(point.last_seen_import_draft_id) ||
    normalizeText(point.last_seen_in_import_at) ||
    normalizeText(point.source_record_key),
  )

type ContactPointCollection<T extends ContactPointLike> =
  | T[]
  | Record<string, unknown>
  | null
  | undefined

export const normalizeContactPointList = <T extends ContactPointLike>(
  contactPoints: ContactPointCollection<T>,
): T[] => {
  if (Array.isArray(contactPoints)) return contactPoints.filter(Boolean)

  if (!contactPoints || typeof contactPoints !== "object") return []

  const items = (contactPoints as { items?: unknown }).items

  if (Array.isArray(items)) return items.filter(Boolean) as T[]

  return Object.values(contactPoints).filter((entry): entry is T =>
    Boolean(entry && typeof entry === "object" && !Array.isArray(entry)),
  )
}

const isUsableContactPoint = (point: ContactPointLike) =>
  !["CANDIDATE", "STALE_CANDIDATE", "INVALID", "DO_NOT_USE", "ARCHIVED"].includes(
    lifecycleStatus(point),
  )

const valuesForKind = (contactPoints: ContactPointCollection<ContactPointLike>, kind: string) =>
  normalizeContactPointList(contactPoints)
    .filter((point) => normalizeKind(point.kind) === kind && isUsableContactPoint(point))
    .map((point) => {
      if (kind === "ADDRESS") {
        const address = formatContactAddress(point.value)

        return address || formatContactAddress(point.normalized_value)
      }

      return normalizeText(point.value) || normalizeText(point.normalized_value)
    })
    .filter(Boolean)

const valuesForPhoneKinds = (contactPoints: ContactPointCollection<ContactPointLike>) =>
  normalizeContactPointList(contactPoints)
    .filter((point) => isPhoneContactPointKind(point.kind) && isUsableContactPoint(point))
    .map((point) => normalizeText(point.value) || normalizeText(point.normalized_value))
    .filter(Boolean)

export const groupContactPointsByCustomerId = <T extends ContactPointLike>(
  contactPoints: ContactPointCollection<T>,
) =>
  normalizeContactPointList(contactPoints).reduce(
    (acc, point) => {
      const customerId = normalizeText(point.customer_id)

      if (!customerId) return acc

      const list = acc[customerId] ?? []
      list.push(point)
      acc[customerId] = list

      return acc
    },
    {} as Record<string, T[]>,
  )

export const resolveContactTagColor = (_contactPoints: ContactPointLike[]): ContactTagColor =>
  "neutral"

export const resolveContactTagIcon = (contactPoints: ContactPointLike[]): string => {
  const hasEmail = valuesForKind(contactPoints, "EMAIL").length > 0
  const hasPhone = valuesForPhoneKinds(contactPoints).length > 0

  if (hasEmail && hasPhone) return mdiCheckCircleOutline

  if (hasPhone) return mdiPhoneOutline

  if (hasEmail) return mdiEmailOutline

  return mdiAlertCircleOutline
}

const uniqueLabels = (labels: Array<string | null | undefined>) =>
  Array.from(new Set(labels.map(normalizeText).filter(Boolean)))

export const contactPointSourceLabel = (source: string | null | undefined) => {
  const normalized = normalizeText(source || "").toUpperCase()

  if (normalized === "MANUAL") return "In KehrBlatt eingetragen"

  if (normalized === "IMPORT") return "Aus Fachprogramm"

  if (["BOT", "VOICE", "PORTAL", "INBOUND_LEARNED"].includes(normalized))
    return "Aus Rückmeldung übernommen"

  return null
}

export const contactPointSourceRank = (source: string | null | undefined) => {
  const normalized = normalizeText(source || "").toUpperCase()

  if (normalized === "MANUAL") return 0

  if (normalized === "IMPORT") return 1

  if (["BOT", "VOICE", "PORTAL", "INBOUND_LEARNED"].includes(normalized)) return 2

  return 3
}

export const isContactPointImportManaged = (point: ContactPointLike) =>
  sourceValue(point) === "IMPORT" || hasImportMetadata(point)

export const contactPointOriginPresentation = (
  point: ContactPointLike,
): ContactMetaTagPresentation => {
  const normalized = sourceValue(point)

  if (normalized === "IMPORT") {
    return {
      label: "Aus Fachprogramm",
      color: "blue",
      tooltip: "",
    }
  }

  if (normalized === "MANUAL") {
    return {
      label: "In KehrBlatt eingetragen",
      color: "#74808c",
      tooltip: "",
    }
  }

  if (["BOT", "VOICE", "PORTAL", "INBOUND_LEARNED"].includes(normalized)) {
    return {
      label: "Aus Rückmeldung übernommen",
      color: "teal",
      tooltip:
        "Dieser Kontaktweg wurde aus einer Termin- oder Ansprechpartner-Rückmeldung übernommen.",
    }
  }

  return {
    label: "Herkunft unbekannt",
    color: "rose",
    tooltip: "",
  }
}

export const contactPointImportSyncPresentation = (
  point: ContactPointLike,
): ContactMetaTagPresentation | null => {
  if (sourceValue(point) === "IMPORT" || !hasImportMetadata(point)) return null

  return {
    label: "Im Fachprogramm gefunden",
    color: "emerald",
    tooltip:
      "Dieser Kontaktweg steht im letzten Import aus dem Fachprogramm. Wenn er dort später fehlt, entfernt KehrBlatt ihn beim Reimport automatisch.",
  }
}

export const contactPointVerificationLabel = (point: ContactPointLike) =>
  point.is_verified ? "verifiziert" : "nicht verifiziert"

export const buildContactPointMetaTooltipFields = (
  point: ContactPointLike,
): ContactPointMetaTooltipField[] => {
  const origin = contactPointOriginPresentation(point)
  const importSync = contactPointImportSyncPresentation(point)

  return [
    { label: "Herkunft", value: origin.label },
    importSync ? { label: "Fachprogramm", value: importSync.label } : null,
    { label: "Prüfung", value: contactPointVerificationLabel(point) },
  ].filter((field): field is ContactPointMetaTooltipField => Boolean(field))
}

const contactPointQualitySourceRank = (point: ContactPointLike) => {
  const normalized = sourceValue(point)

  if (normalized === "MANUAL") return 0

  if (isContactPointImportManaged(point)) return 1

  return contactPointSourceRank(normalized)
}

const createdAtRank = (point: ContactPointLike) => {
  const value = normalizeText(point.created_at)

  if (!value) return Number.MAX_SAFE_INTEGER

  const timestamp = new Date(value).getTime()

  return Number.isFinite(timestamp) ? -timestamp : Number.MAX_SAFE_INTEGER
}

export const contactPointQualitySortKey = (point: ContactPointLike) =>
  [
    Number(!isUsableContactPoint(point)),
    Number(!point.is_verified),
    contactPointQualitySourceRank(point),
    createdAtRank(point),
    normalizeText(point.id),
  ] as const

export const sortContactPointsByQuality = <T extends ContactPointLike>(contactPoints: T[]) =>
  (contactPoints ?? []).slice().sort((left, right) => {
    const leftKey = contactPointQualitySortKey(left)
    const rightKey = contactPointQualitySortKey(right)
    for (let index = 0; index < leftKey.length; index += 1) {
      const leftValue = leftKey[index]
      const rightValue = rightKey[index]

      if (leftValue === rightValue) continue

      if (typeof leftValue === "number" && typeof rightValue === "number")
        return leftValue - rightValue

      return String(leftValue).localeCompare(String(rightValue), "de")
    }

    return 0
  })

export const buildContactQualityLabels = (contactPoints: ContactPointLike[]) => {
  const points = (contactPoints ?? []).filter(isUsableContactPoint)
  const sources = uniqueLabels(points.map((point) => contactPointOriginPresentation(point).label))
  const importSync = uniqueLabels(
    points.map((point) => contactPointImportSyncPresentation(point)?.label),
  )
  const verification = uniqueLabels([
    points.some((point) => point.is_verified) ? "verifiziert" : null,
    points.some((point) => !point.is_verified) ? "nicht verifiziert" : null,
  ])

  return [...sources, ...importSync, ...verification]
}

const truncateContactTagName = (value: string, maxLength = 8) => {
  const normalized = normalizeText(value)

  if (normalized.length <= maxLength) return normalized

  return `${normalized.slice(0, maxLength).trimEnd()}...`
}

const contactTagLabel = (baseLabel: string, roleLabel?: string | null) =>
  normalizeText(roleLabel)
    ? `${normalizeText(roleLabel)} (${truncateContactTagName(baseLabel)})`
    : baseLabel

export const buildContactTag = ({
  id,
  customer,
  fallbackLabel,
  contactPoints,
  unitLabel,
  roleLabel,
}: {
  id: string
  customer?: CustomerLike | null
  fallbackLabel?: string | null
  contactPoints: ContactPointLike[]
  unitLabel?: string | null
  roleLabel?: string | null
  showRoleInLabel?: boolean
  contactReasonLabel?: string | null
  sequenceLabel?: string | null
}): ContactTagPresentation => {
  const baseLabel =
    normalizeText(customer?.display_name) ||
    normalizeText(fallbackLabel) ||
    "Unbekannter Ansprechpartner"
  const emails = valuesForKind(contactPoints, "EMAIL")
  const mobiles = valuesForKind(contactPoints, "MOBILE")
  const fixedLines = valuesForKind(contactPoints, "FIXED_LINE")
  const phones = [...mobiles, ...fixedLines]
  const normalizedRole = normalizeText(roleLabel) || "Nicht hinterlegt"

  return {
    id,
    label: contactTagLabel(baseLabel, roleLabel),
    roleLabel: normalizedRole,
    nameLabel: baseLabel,
    icon: resolveContactTagIcon(contactPoints),
    color: resolveContactTagColor(contactPoints),
    tooltip: buildRichTooltip([
      roleLabel && contactTagLabel(baseLabel, roleLabel).includes("...")
        ? tooltipTitle(baseLabel)
        : null,
      tooltipField("Zuständig für", unitLabel),
      tooltipField("E-Mail", emails[0]),
      tooltipField("Telefon", phones[0]),
      !emails.length && !phones.length
        ? tooltipField("Adresse", valuesForKind(contactPoints, "ADDRESS")[0])
        : null,
      !emails.length && !phones.length
        ? tooltipField("Hinweis", "Bitte eine E-Mail-Adresse oder Telefonnummer ergänzen.")
        : null,
    ]),
    tooltipHtml: true,
  }
}

export const formatPropertyUnitIdentifier = (
  property?: { units?: Array<Record<string, unknown>> | null } | null,
  propertyUnitId?: string | null,
) => {
  return formatPropertyUnitDisplayForProperty(property, propertyUnitId)
}

export const formatPropertyAddressLines = (
  property?: {
    street?: string | null
    house_number?: string | null
    zip?: string | null
    city?: string | null
    country?: string | null
    address_extra?: string | null
    units?: Array<Record<string, unknown>> | null
  } | null,
  propertyUnitId?: string | null,
) => {
  if (!property) return ["Unbekannte Liegenschaft"]

  return formatPropertyAddressLinesWithUnit(property as Property, propertyUnitId)
}
